(() => {
  'use strict';

  const GLOBAL_KEY = '__pbHomeCarouselA11y';
  const RAIL_SELECTOR = "[data-testid='pb-product-rail']";
  const SLIDE_SELECTOR = `${RAIL_SELECTOR} slideshow-slide`;
  const SELECT_EVENT = 'slideshow:select';
  const previous = window[GLOBAL_KEY];
  if (previous && typeof previous.destroy === 'function') previous.destroy();

  const defer = (callback) => {
    if (typeof queueMicrotask === 'function') queueMicrotask(callback);
    else Promise.resolve().then(callback);
  };

  const createController = () => {
    let mounted = false;
    let reconcileQueued = false;
    let discoveryObserver = null;
    const railObservers = new Map();
    const cleanups = [];

    const on = (target, type, handler, options) => {
      if (!target || typeof target.addEventListener !== 'function') return;
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    };

    const isElement = (node) => Boolean(node && typeof node.matches === 'function');
    const isConnected = (node) => Boolean(node && node.isConnected !== false);

    const syncSlide = (slide) => {
      if (!isElement(slide) || !slide.matches(SLIDE_SELECTOR)) return;
      const hidden = slide.getAttribute('aria-hidden') === 'true';
      if (hidden) {
        if (!slide.hasAttribute('inert')) slide.setAttribute('inert', '');
      } else if (slide.hasAttribute('inert')) {
        slide.removeAttribute('inert');
      }
    };

    const syncRail = (rail) => {
      if (!isElement(rail) || !rail.matches(RAIL_SELECTOR) || typeof rail.querySelectorAll !== 'function') return;
      Array.from(rail.querySelectorAll(SLIDE_SELECTOR)).forEach(syncSlide);
    };

    const queueReconcile = () => {
      if (!mounted || reconcileQueued) return;
      reconcileQueued = true;
      defer(() => {
        reconcileQueued = false;
        reconcile();
      });
    };

    const stopRailObserver = (rail) => {
      const observer = railObservers.get(rail);
      if (!observer) return;
      observer.disconnect();
      railObservers.delete(rail);
    };

    const observeRail = (rail) => {
      if (!isElement(rail) || !rail.matches(RAIL_SELECTOR)) return;
      if (!railObservers.has(rail) && typeof window.MutationObserver === 'function') {
        const observer = new window.MutationObserver((records) => {
          if (!mounted || !isConnected(rail)) return;
          records.forEach((record) => {
            if (record.type === 'attributes' && isElement(record.target)) syncSlide(record.target);
            if (record.type === 'childList') syncRail(rail);
          });
        });
        observer.observe(rail, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['aria-hidden']
        });
        railObservers.set(rail, observer);
      }
      syncRail(rail);
    };

    const stopDiscoveryObserver = () => {
      if (discoveryObserver) discoveryObserver.disconnect();
      discoveryObserver = null;
    };

    const ensureDiscoveryObserver = () => {
      if (!mounted || discoveryObserver || !document.body || typeof window.MutationObserver !== 'function') return;
      discoveryObserver = new window.MutationObserver(() => {
        if (mounted) queueReconcile();
      });
      discoveryObserver.observe(document.body, { childList: true, subtree: true });
    };

    const reconcile = () => {
      if (!mounted || !document.querySelectorAll) return;
      ensureDiscoveryObserver();
      const rails = new Set(Array.from(document.querySelectorAll(RAIL_SELECTOR)));
      railObservers.forEach((_observer, rail) => {
        if (!rails.has(rail) || !isConnected(rail)) stopRailObserver(rail);
      });
      rails.forEach(observeRail);
    };

    const onSectionUnload = (event) => {
      if (!mounted) return;
      const target = event && event.target;
      if (!target || typeof target.querySelector !== 'function' || target.querySelector(RAIL_SELECTOR)) queueReconcile();
    };

    const mount = () => {
      if (mounted) return;
      mounted = true;
      on(document, SELECT_EVENT, queueReconcile, true);
      on(document, 'shopify:section:load', queueReconcile, true);
      on(document, 'shopify:section:unload', onSectionUnload, true);
      on(window, 'pagehide', destroy, { once: true });
      if (document.body) reconcile();
      else on(document, 'DOMContentLoaded', reconcile, { once: true });
    };

    const destroy = () => {
      if (!mounted) return;
      mounted = false;
      reconcileQueued = false;
      stopDiscoveryObserver();
      railObservers.forEach((_observer, rail) => stopRailObserver(rail));
      cleanups.splice(0).forEach((cleanup) => cleanup());
      if (window[GLOBAL_KEY] === api) delete window[GLOBAL_KEY];
    };

    const api = { mount, destroy, syncSlide };
    return api;
  };

  const api = createController();
  window[GLOBAL_KEY] = api;
  api.mount();
})();
